import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { convertToInputModel } from "./composition/convert-to-input-model";
import { useFetchNewPriceRankItem } from "../../repository/query/fetch-new-room-price-rank-item/use-fetch-new-room-price-rank-item";
import { excludeKeys, type ExcludeKeys, type ProductRoomPriceRankFormModel } from "../../type";
import { roomPriceRankMastInputZodSchema } from "../../validation-schema";

import type { ServerModel } from "./type";
import type { RoomPriceRankMastInputZodSchemaType } from "../../validation-schema";

type Props = {
	serverModel: ServerModel;
};

type ViewModel = ProductRoomPriceRankFormModel["ViewModel"];
type InputModel = ProductRoomPriceRankFormModel["InputModel"];
type QueryData = ProductRoomPriceRankFormModel["QueryData"];

export type ProductRoomPriceRankOnSetViewModelType = <K extends keyof ViewModel>(
	values: { key: K; value: ViewModel[K] }[],
) => void;

export const useRoomPriceRankModel = ({ serverModel }: Props) => {
	const { hotelID } = serverModel.roomPriceRank;

	const inputModel = convertToInputModel({ serverModel });

	const initialViewModel = inputModelToViewModelConverter<
		InputModel,
		ExcludeKeys,
		QueryData,
		RoomPriceRankMastInputZodSchemaType
	>({
		defaultInput: inputModel,
		initInput: undefined,
		currentInput: inputModel,
		excludeKeys,
		zodSchema: roomPriceRankMastInputZodSchema,
	});

	const [viewModel, setViewModel] = useState(initialViewModel);

	const onSetViewModel: ProductRoomPriceRankOnSetViewModelType = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			};

			const convertedInputModel = viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const convertedViewModel = inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				RoomPriceRankMastInputZodSchemaType
			>({
				defaultInput: inputModel,
				initInput: inputModel,
				currentInput: convertedInputModel,
				excludeKeys,
				zodSchema: roomPriceRankMastInputZodSchema,
			});

			setViewModel(convertedViewModel);
		},
		[inputModel, viewModel],
	);

	const { refetch: fetchNewPriceRankItem } = useFetchNewPriceRankItem({ hotelID });

	const reset = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	return {
		roomPriceRank: viewModel,
		onSetRoomPriceRank: onSetViewModel,

		reset,

		fetchNewPriceRankItem,
	};
};
